import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ListingCard from "@/components/ListingCard";
import RatingDialog from "@/components/RatingDialog";
import PayoutsCard from "@/components/PayoutsCard";
import DisputeDialog from "@/components/DisputeDialog";
import { toast } from "sonner";
import { Check, X, Plus, RotateCcw, Star, Shield } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  awaiting_payment: "bg-orange-100 text-orange-800",
  confirmed: "bg-green-100 text-green-800",
  approved: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-600",
  expired: "bg-gray-100 text-gray-500",
  completed: "bg-blue-100 text-blue-800",
};

const STATUS_LABELS = {
  pending: "Pending",
  awaiting_payment: "Awaiting Payment",
  confirmed: "Confirmed",
  approved: "Confirmed",
  declined: "Declined",
  cancelled: "Cancelled",
  expired: "Expired",
  completed: "Completed",
};

const depositLabel = (s) =>
  s === "refunded" ? "released" : s === "waived" ? "waived" : s === "pending" ? "not yet held" : "held";

const resolveImg = (img) => !img ? "https://images.unsplash.com/photo-1581147036324-c1c89c2c8b5c?w=400&q=80" : img.startsWith("http") ? img : fileUrl(img);

function BookingRow({ b, mine, onAction, onPay, onReturn, onRate, onReport, onCancel, rated }) {
  const canRate = b.return_status === "returned" && !rated;
  return (
    <div data-testid={`booking-row-${b.id}`} className="flex items-center gap-4 bg-white border border-black/10 rounded-2xl p-4">
      <img src={resolveImg(b.listing_image)} alt="" className="w-16 h-16 rounded-xl object-cover" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{b.listing_title}</p>
        <p className="text-sm text-muted-foreground">{b.start_date} → {b.end_date} · {b.days}d</p>
        <p className="text-sm">{mine ? `Owner: ${b.owner_name}` : `Renter: ${b.renter_name}`}</p>
        {b.deposit_amount > 0 && (
          <p className="text-xs flex items-center gap-1 mt-0.5 text-muted-foreground">
            <Shield className="w-3 h-3" /> Deposit €{b.deposit_amount} · {depositLabel(b.deposit_status)}
          </p>
        )}
        {mine && b.status === "pending" && (
          <p data-testid={`awaiting-owner-${b.id}`} className="text-xs text-amber-700 mt-0.5">Waiting for the owner to accept your request.</p>
        )}
        {mine && b.status === "awaiting_payment" && (
          <p className="text-xs text-orange-700 mt-0.5">Accepted! Pay within 24 hours to confirm your booking.</p>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="font-display font-bold text-lg">€{b.total_amount}</p>
        <div className="flex items-center gap-1.5 justify-end mt-1">
          <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLES[b.status] || "bg-gray-100 text-gray-600"}`}>{STATUS_LABELS[b.status] || b.status}</span>
          {b.payment_status === "paid" && <span className="text-[11px] font-bold uppercase px-2 py-0.5 rounded-full bg-green-100 text-green-800">paid</span>}
        </div>
        {!mine && b.status === "pending" && (
          <div className="flex gap-2 mt-2 justify-end">
            <button data-testid={`approve-${b.id}`} onClick={() => onAction(b.id, "approved")} className="p-1.5 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition-colors"><Check className="w-4 h-4" /></button>
            <button data-testid={`decline-${b.id}`} onClick={() => onAction(b.id, "declined")} className="p-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"><X className="w-4 h-4" /></button>
          </div>
        )}
        {mine && b.status === "awaiting_payment" && (
          <button data-testid={`pay-${b.id}`} onClick={() => onPay(b.id)} className="mt-2 text-xs font-medium bg-[#0A0A0A] text-white rounded-full px-3 py-1.5 hover:bg-[#FF5A00] transition-colors">Pay now</button>
        )}
        {mine && onCancel && ["pending", "awaiting_payment"].includes(b.status) && (
          <button data-testid={`cancel-${b.id}`} onClick={() => onCancel(b.id)} className="mt-2 block ml-auto text-xs font-medium text-muted-foreground hover:text-red-600 transition-colors">Cancel request</button>
        )}
        {!mine && b.status === "confirmed" && b.payment_status === "paid" && b.return_status !== "returned" && (
          <button data-testid={`return-${b.id}`} onClick={() => onReturn(b)} className="mt-2 flex items-center gap-1 text-xs font-medium bg-[#0A0A0A] text-white rounded-full px-3 py-1.5 hover:bg-[#FF5A00] transition-colors">
            <RotateCcw className="w-3 h-3" /> Confirm return
          </button>
        )}
        {canRate && (
          <button data-testid={`rate-booking-${b.id}`} onClick={() => onRate(b)} className="mt-2 flex items-center gap-1 text-xs font-medium border border-black/20 rounded-full px-3 py-1.5 hover:border-black transition-colors">
            <Star className="w-3 h-3" /> Rate {mine ? "owner" : "renter"}
          </button>
        )}
        {b.payment_status === "paid" && onReport && (
          <button data-testid={`report-${b.id}`} onClick={() => onReport(b)} className="mt-2 block ml-auto text-xs font-medium text-muted-foreground hover:text-red-600 transition-colors">Report issue</button>
        )}
      </div>
    </div>
  );
}

function PurchaseRow({ p, mine, onRate, rated }) {
  const canRate = p.payment_status === "paid" && !rated;
  return (
    <div data-testid={`purchase-row-${p.id}`} className="flex items-center gap-4 bg-white border border-black/10 rounded-2xl p-4">
      <img src={resolveImg(p.listing_image)} alt="" className="w-16 h-16 rounded-xl object-cover" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{p.listing_title}</p>
        <p className="text-sm">{mine ? `Seller: ${p.seller_name}` : `Buyer: ${p.buyer_name}`}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-display font-bold text-lg">€{p.amount}</p>
        <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${p.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
          {p.payment_status === "paid" ? "Paid" : "Pending"}
        </span>
        {canRate && (
          <button data-testid={`rate-purchase-${p.id}`} onClick={() => onRate(p)} className="mt-2 flex items-center gap-1 text-xs font-medium border border-black/20 rounded-full px-3 py-1.5 hover:border-black transition-colors ml-auto">
            <Star className="w-3 h-3" /> Rate {mine ? "seller" : "buyer"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [requests, setRequests] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [given, setGiven] = useState([]);
  const [myDisputes, setMyDisputes] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [rateTarget, setRateTarget] = useState(null);
  const [rateOpen, setRateOpen] = useState(false);
  const [disputeTarget, setDisputeTarget] = useState(null);
  const [disputeOwner, setDisputeOwner] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);

  const load = () => {
    api.get("/listings", { params: { owner_id: user.user_id } }).then((r) => setListings(r.data));
    api.get("/bookings/my-rentals").then((r) => setRentals(r.data));
    api.get("/bookings/my-requests").then((r) => setRequests(r.data));
    api.get("/purchases/my-purchases").then((r) => setPurchases(r.data));
    api.get("/purchases/my-sales").then((r) => setSales(r.data));
    api.get("/user-reviews/given").then((r) => setGiven(r.data));
    api.get("/disputes/mine").then((r) => setMyDisputes(r.data));
    api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  const isRated = (refId) => given.some((g) => g.ref_id === refId);
  const openReport = (b, isOwner) => { setDisputeTarget(b); setDisputeOwner(isOwner); setDisputeOpen(true); };

  const handleAction = async (id, status) => {
    try { await api.post(`/bookings/${id}/status`, { status }); toast.success(`Booking ${status === "approved" ? "accepted" : status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const handleCancel = async (id) => {
    try { await api.post(`/bookings/${id}/status`, { status: "cancelled" }); toast.success("Request cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  const handleReturn = async (b) => {
    try {
      const res = await api.post(`/bookings/${b.id}/return`);
      toast.success(res.data.refunded ? `Marked returned · €${res.data.deposit} deposit refunded` : "Marked as returned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to mark returned"); }
  };

  const openRate = (target) => { setRateTarget(target); setRateOpen(true); };
  const rateBooking = (b) => openRate({
    ratee_id: b.owner_id === user.user_id ? b.renter_id : b.owner_id,
    ratee_name: b.owner_id === user.user_id ? b.renter_name : b.owner_name,
    ref_id: b.id, context: "rental",
    role: b.owner_id === user.user_id ? "Your renter" : "The item owner",
  });
  const ratePurchase = (p) => openRate({
    ratee_id: p.seller_id === user.user_id ? p.buyer_id : p.seller_id,
    ratee_name: p.seller_id === user.user_id ? p.buyer_name : p.seller_name,
    ref_id: p.id, context: "sale",
    role: p.seller_id === user.user_id ? "Your buyer" : "The seller",
  });

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Manage your listings and rentals.</p>
        </div>
        <button data-testid="dash-new-listing" onClick={() => navigate("/create")} className="hidden sm:flex items-center gap-1.5 bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#FF5A00] transition-colors"><Plus className="w-4 h-4" /> New listing</button>
      </div>

      <div className="mt-6"><PayoutsCard /></div>

      <Tabs defaultValue="listings" className="mt-8">
        <TabsList className="bg-[#E3E8E1] rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-listings" value="listings" className="rounded-full data-[state=active]:bg-white">My Listings ({listings.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-rentals" value="rentals" className="rounded-full data-[state=active]:bg-white">My Rentals ({rentals.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-requests" value="requests" className="rounded-full data-[state=active]:bg-white">Requests ({requests.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-purchases" value="purchases" className="rounded-full data-[state=active]:bg-white">Purchases ({purchases.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-sales" value="sales" className="rounded-full data-[state=active]:bg-white">Sales ({sales.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-saved" value="saved" className="rounded-full data-[state=active]:bg-white">Saved ({favorites.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-disputes" value="disputes" className="rounded-full data-[state=active]:bg-white">Claims ({myDisputes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-6">
          {listings.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-black/10">
              <p className="text-muted-foreground">You haven't listed anything yet.</p>
              <button onClick={() => navigate("/create")} className="mt-3 bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#FF5A00] transition-colors">List your first item</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} owner />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rentals" className="mt-6 space-y-3">
          {rentals.length === 0 ? <p className="text-muted-foreground text-center py-12">No rentals yet.</p>
            : rentals.map((b) => <BookingRow key={b.id} b={b} mine onPay={(id) => navigate(`/booking/${id}`)} onRate={rateBooking} onReport={(bk) => openReport(bk, false)} onCancel={handleCancel} rated={isRated(b.id)} />)}
        </TabsContent>

        <TabsContent value="requests" className="mt-6 space-y-3">
          {requests.length === 0 ? <p className="text-muted-foreground text-center py-12">No incoming requests.</p>
            : requests.map((b) => <BookingRow key={b.id} b={b} mine={false} onAction={handleAction} onReturn={handleReturn} onRate={rateBooking} onReport={(bk) => openReport(bk, true)} rated={isRated(b.id)} />)}
        </TabsContent>

        <TabsContent value="purchases" className="mt-6 space-y-3">
          {purchases.length === 0 ? <p className="text-muted-foreground text-center py-12">You haven't bought anything yet.</p>
            : purchases.map((p) => <PurchaseRow key={p.id} p={p} mine onRate={ratePurchase} rated={isRated(p.id)} />)}
        </TabsContent>

        <TabsContent value="sales" className="mt-6 space-y-3">
          {sales.length === 0 ? <p className="text-muted-foreground text-center py-12">No sales yet.</p>
            : sales.map((p) => <PurchaseRow key={p.id} p={p} mine={false} onRate={ratePurchase} rated={isRated(p.id)} />)}
        </TabsContent>

        <TabsContent value="saved" className="mt-6">
          {favorites.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-black/10">
              <p className="text-muted-foreground">No saved items yet. Tap the heart on any listing to save it for later.</p>
              <button onClick={() => navigate("/browse")} className="mt-3 bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#FF5A00] transition-colors">Browse items</button>
            </div>
          ) : (
            <div data-testid="saved-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {favorites.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="disputes" className="mt-6 space-y-3">
          {myDisputes.length === 0 ? <p className="text-muted-foreground text-center py-12">No claims filed. Use "Report issue" on a paid booking if something goes wrong.</p>
            : myDisputes.map((d) => (
              <div key={d.id} data-testid={`my-dispute-${d.id}`} className="bg-white border border-black/10 rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${d.type === "non_return" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{d.type === "non_return" ? "Non-return" : "Dispute"}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${d.status === "open" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>{d.status}</span>
                </div>
                <p className="font-semibold mt-1.5">{d.subject}</p>
                <p className="text-sm text-muted-foreground">{d.listing_title} · {d.filer_id === user.user_id ? `you vs ${d.against_name}` : `${d.filer_name} vs you`}</p>
                <p className="text-sm mt-1">{d.description}</p>
                {d.resolution && <p className="text-xs text-[#2E7D32] mt-2 font-medium">Resolution: {d.resolution}</p>}
              </div>
            ))}
        </TabsContent>
      </Tabs>

      <RatingDialog open={rateOpen} onOpenChange={setRateOpen} target={rateTarget} onDone={load} />
      <DisputeDialog open={disputeOpen} onOpenChange={setDisputeOpen} booking={disputeTarget} isOwner={disputeOwner} onDone={load} />
    </div>
  );
}
